/**
 * "Back to where you came from", on the two detail screens.
 *
 * The reason this is not a plain `<Link to="/sims">` is the estate's URL. An
 * operator reaches a SIM from a filtered, paged, basket-scoped list — and that
 * state lives entirely in the query string, by design, so a view can be pasted
 * into a support thread. A fixed link would drop all of it and put them back on
 * page 1 of an unfiltered estate, which is the thing this button exists to
 * avoid.
 *
 * `navigate(-1)` restores it exactly, because the browser kept it. The catch is
 * a URL opened cold — a bookmark, a pasted link, a refresh — where there is no
 * previous entry and going back would leave the panel entirely. React Router
 * marks that case: `location.key` is the literal `'default'` only for the first
 * entry of a history session. So the control falls back to the list, and says
 * so — the label names the destination when it cannot simply reverse.
 */

import { ChevronLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

export function BackLink({
  fallback,
  fallbackLabel,
}: {
  /** Where to go when there is no history to go back to. */
  fallback: string;
  /** What that destination is called, for the label and the accessible name. */
  fallbackLabel: string;
}): ReactElement {
  const navigate = useNavigate();
  const { key } = useLocation();
  const canReverse = key !== 'default';
  const label = canReverse ? 'Back' : fallbackLabel;

  return (
    <button
      type="button"
      onClick={() => {
        if (canReverse) navigate(-1);
        else void navigate(fallback);
      }}
      // A real button, not a styled span: it is operated by keyboard and by
      // screen reader, and it performs navigation rather than following an
      // href, so an anchor would be lying about what happens on click.
      aria-label={canReverse ? `Back to ${fallbackLabel}` : `Go to ${fallbackLabel}`}
      className="mb-[var(--space-3)] -ml-[var(--space-1)] inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-[var(--space-1)] py-[var(--space-1)] text-[length:var(--text-sm)] font-medium text-muted transition-colors duration-[var(--motion-fast)] hover:text-body focus-visible:text-body"
    >
      <ChevronLeft aria-hidden="true" size={16} strokeWidth={2} className="shrink-0" />
      {label}
    </button>
  );
}
